import { z } from '@customerio/hydra-validation';
import type {
  IntegrationsSegmentOutUpdateFields,
  SegmentOutIntegration as UIAPISegmentOutIntegration,
} from '@customerio/hydra-ui-api';
import { defineEntity, readSchema } from '../shared/define-entity';

/**
 * A workspace's Segment source connector — the record behind
 * `/integrations/segment_out`.
 *
 * One per workspace, so the entity is keyed on the sole record rather than an
 * id. `created_at` / `last_activity_at` stay raw unix seconds, matching the
 * sibling connector entities. `events` is the one field normalized: the backend
 * omits it for a connector with none selected, and every consumer wants a list.
 *
 * `use_javascript` separates the two generations of this integration: the
 * current one sets it on create, and a record without it predates that and is
 * served by the legacy `segdotcom` endpoint instead.
 */
export type SegmentOutIntegration = Omit<UIAPISegmentOutIntegration, 'events'> & {
  events: string[];
};

const SegmentOutIntegrationFromApi = readSchema<SegmentOutIntegration>(
  z.looseObject({
    events: z
      .array(z.string())
      .nullish()
      .transform((events) => events ?? []),
  }),
);

/** The settings the config form saves. The key is not editable. */
export type SegmentOutSettingsInput = IntegrationsSegmentOutUpdateFields;

/** @public */
export const segmentOutIntegration = defineEntity<
  SegmentOutIntegration,
  SegmentOutSettingsInput,
  void,
  undefined,
  'workspace'
>({
  name: 'segment-out-integration',
  scope: 'workspace',
  pk: (integration) => String(integration.id),
  schema: { read: SegmentOutIntegrationFromApi },
});

export const segmentOutIntegrationKeys = segmentOutIntegration.keys;

export function parseSegmentOutIntegration(raw: UIAPISegmentOutIntegration): SegmentOutIntegration {
  return segmentOutIntegration.parse(raw);
}

/**
 * Whether the record predates the current integration.
 *
 * A legacy record is configured through the `segdotcom` endpoint, not this one,
 * so the page hands it to a different form.
 */
export function isLegacySegmentOut(integration: Pick<SegmentOutIntegration, 'use_javascript'>): boolean {
  return !integration.use_javascript;
}

/**
 * Saving needs at least one event selected.
 *
 * Ember paired this with a dirty check and the edit permission, which are the
 * caller's halves. Note what it implies: a connector whose events have all been
 * unchecked cannot be saved — it can only be disabled or removed, which is the
 * behaviour the empty list is meant to prevent reaching by accident.
 */
export function canSaveSegmentOutSettings(events: readonly string[]): boolean {
  return events.length > 0;
}

/**
 * Whether the enable toggle is stuck off.
 *
 * Only a connector that is *currently off* with nothing selected is locked —
 * turning one off is always allowed. A record can be enabled with an empty event
 * list, and locking the toggle then would leave no way to switch it off.
 * Unchecking every event while enabled reaches the same state through the UI.
 */
export function isSegmentOutEnableLocked(settings: { enabled: boolean; events: readonly string[] }): boolean {
  return !settings.enabled && settings.events.length === 0;
}

/**
 * Whether saving should also switch the connector on.
 *
 * Ember only ever enabled implicitly, and only for a connector that had no events
 * to begin with: choosing the first events is what turns it on. A connector that
 * already had events keeps whatever state it was in, so re-saving never silently
 * re-enables one the reader had disabled.
 */
export function shouldEnableOnSave(settings: { enabled: boolean; originalEvents: readonly string[] }): boolean {
  return settings.originalEvents.length === 0 && !settings.enabled;
}
